import { useState, useEffect } from "react";
import {
  DataGrid,
  GridPaginationModel,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import {
  ProductListGridColumns,
  ProductListReferenceColumnsMapping,
} from "./ProductContstants";
import api from "../../services/ApiService";
import { ApiResponse } from "../../models/ApiResponse";
import { ItemBasicDetail } from "../../models/Items/ItemBasicDetail";
import {
  SelectedFilters,
} from "../../models/Items/ItemListRequest";
import { PermissionsFriendlyNames, SortDirection } from "../../models/Enum";
import Filters from "./Filters";
import {
  Box,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Switch,
} from "@mui/material";
import Loader from "../../components/Loader";
import { NO_LOCAL_SEARCH_RESULTS_GRID_MESSAGE, NO_ROWS_GRID_MESSAGE } from "../../components/GridUtilityComponents";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../store";
import { updateInactiveItemListRequest } from "../../reducers/inactiveItemsReducer";
import DeleteValidationModal from "../../components/ManageSettingComponents/DeleteValidationModal";
import { MULTI_SELECT_FIELD_NAMES, PRODUCT_TYPE_DROPDOWN_OPTIONS, VIEW_INACTIVE_PRODUCTS } from "./EditItem/Constants";
import GridCellChipsContent from "../../components/GridCellChipsContent";
import ConfirmationModal from "../../components/ConfirmationModal";

export interface InactiveProductListResponse {
  items?: ({
    isActive: boolean;
  } & ItemBasicDetail)[];
  totalCount?: number;
}

export const initialItemListRequest = {
  pageIndex: 1,
  pageSize: 10,
  sortBy: "updatedDate",
  sortDirection: SortDirection.DESC,
  [MULTI_SELECT_FIELD_NAMES.developmentType]: [PRODUCT_TYPE_DROPDOWN_OPTIONS[0].id],
  hideReleasedToInriver: false,
  hideArchivedDevelopment: false,
};

export default function InactiveProductList() {
  const [isLoading, setLoading] = useState(false);
  const [items, setItems] = useState<ItemBasicDetail[]>([]);

  const [selectedRows, setSelectedRows] = useState([]);
  const [allRecordsSelected, setAllRecordsSelected] = useState(false);

  const [productsTotalCount, setProductsTotalCount] = useState(0);
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(true);
  const [projectId, setProjectId] = useState(0);
  const [expandedRowIds, setExpandedRowIds] = useState<number[]>([]);
  const [activateProductModal, setActivateProductModal] = useState(false);
  const dispatch = useAppDispatch();

  const itemListRequestState = useAppSelector((state) => state.inactiveItems.itemListRequest);

  const handleActivateProduct = async () => {
    const data = {
      id: projectId,
      isActive: true
    };
    try {
      api
        .patch<ApiResponse<boolean>>("/item/active-deactive", data)
        .then(async (response) => {
          if (response.isSuccess) {
            toast.success(response.message);
            setActivateProductModal(false);
            await getItems();
          } else {
            throw new Error(response.message);
          }
        })
        .catch(() => { });
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const stageStatusColumn = [
    { field: "id", headerName: "Id", width: 70, disableColumnMenu: true },
    { field: "updatedDate", headerName: "Updated Date", width: 120, disableColumnMenu: true, sortable: true, hide: true },
    {
      field: "isActive", sortable: false, disableColumnMenu: true, headerName: "Is Active", width: 100,
      renderCell: ({ row }) =>
        <Switch
          checked={row?.isActive}
          onChange={async (e) => {
            setProjectId(row?.id);
            setActivateProductModal(true);
          }}
          color="primary"
          inputProps={{ "aria-label": "controlled" }}
        />
    },
    {
      field: "stockcode",
      headerName: "Stock Code",
      sortable: true,
      disableColumnMenu: true,
      width: 180
    },
    {
      field: "pdStockcode",
      headerName: "PD StockCode",
      sortable: true,
      disableColumnMenu: true,
      width: 180,
    },
    {
      field: "baseCode",
      headerName: "Base Code",
      sortable: true,
      disableColumnMenu: true,
      width: 125,
    },
    {
      field: "pdCode",
      headerName: "PD BaseCode",
      sortable: true,
      disableColumnMenu: true,
      width: 120,
    },
    {
      field: "brandCollectionDisplayText",
      headerName: "Brand Collection",
      sortable: true,
      disableColumnMenu: true,
      width: 130
    },
    {
      field: "shortDesc",
      headerName: "Short Description",
      sortable: false,
      disableColumnMenu: true,
      width: 250
    },
    {
      field: "stageDisplayText",
      headerName: "Stage",
      sortable: true,
      disableColumnMenu: true,
      width: 120,
    },
    {
      field: "statusDisplayText",
      headerName: "Status",
      sortable: true,
      disableColumnMenu: true,
      width: 155,
    },
    {
      field: "tagsDisplay",
      headerName: "Tags",
      sortable: false,
      disableColumnMenu: true,
      width: 350,
      renderCell: (params) => {
        const tagsWithId = Array.isArray(params.value)
          ? params.value.map((tag, index) =>
            typeof tag === "string"
              ? { id: index + 1, name: tag }
              : tag
          )
          : [];

        return (
          <GridCellChipsContent
            keyField="id"
            displayTextFiled="name"
            cellData={tagsWithId?.length > 3 && !expandedRowIds?.includes(params.row?.id) ? tagsWithId.filter((_, i) => i < 3) : tagsWithId}
            showViewMore={tagsWithId?.length > 3}
            viewMoreText={expandedRowIds?.includes(params.row?.id) ? "Show Less" : "Show More"}
            viewMoreHandle={() => setExpandedRowIds(expandedRowIds?.includes(params.row?.id) ? expandedRowIds.filter(itemId => itemId !== params.row?.id) : [...expandedRowIds, params.row?.id])}
          />
        );
      }
    },
    {
      field: "vendorDisplayText",
      headerName: "Vendor",
      sortable: true,
      disableColumnMenu: true,
      width: 130
    }
  ];

  const handleCloseModal = () => {
    setActivateProductModal(false);
  };

  const gridToolbar = () => (
    <GridToolbarContainer>
      <div style={{ marginLeft: "12px" }}>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
      </div>
      <div style={{
        flexGrow: 1, textAlign: "center", padding: 5,
        display: "flex", justifyContent: "flex-end"
      }}>
        <GridToolbarQuickFilter sx={{ margin: "5px 30px 5px 5px", width: "30%" }} />
      </div>
    </GridToolbarContainer>
  );

  useEffect(() => {
    getItems();
  }, [itemListRequestState]);

  const getItems = async () => {
    try {
      setLoading(true);
      api
        .post<ApiResponse<InactiveProductListResponse>>("/item/list", {...itemListRequestState, isActive: false})
        .then((response) => {
          setLoading(false);
          if (response.isSuccess) {
            setProductsTotalCount(response?.data?.totalCount);
            const items: ItemBasicDetail[] = (response?.data?.items || []);
            setItems(items);
            // if all rows are selected then update the selectedRows state so that grid reflects with all rows selected
            if (allRecordsSelected) {
              const itemsForSelection = items.map(i => {
                return {
                  id: i.id,
                  stockCode: i?.stockcode,
                  baseCode: i?.baseCode
                };
              });
              setSelectedRows(selectedRows.concat(itemsForSelection.filter(ii => !selectedRows.some(sr => sr.id === ii.id))));
            }
          } else {
            throw new Error(response.message);
          }
        })
        .catch((error) => {
          setLoading(false);
          console.error("Exception from product list", error);
        });
    } catch (error: any) {
      setLoading(false);
      console.error("Exception from product list", error);
    }
  };

  // if sorted by a field which is not direct field of product table then find the correct column from mapping
  const getSortFieldName = (gridFieldName: string): string => {
    return ProductListReferenceColumnsMapping[gridFieldName] || gridFieldName;
  };

  // convert state sortBy value to column name that is bound in grid
  const getReverseSortFieldName = (fieldName: string): string => {
    const gridFieldName = Object.keys(ProductListReferenceColumnsMapping).find(
      (prmKey) => ProductListReferenceColumnsMapping[prmKey] === fieldName
    );
    return gridFieldName || fieldName;
  };

  const onSortChange = (sorting: GridSortModel) => {
    if (sorting?.length === 0) {
      dispatch(updateInactiveItemListRequest({
        ...itemListRequestState,
        sortBy: itemListRequestState.sortBy || initialItemListRequest.sortBy,
        sortDirection: itemListRequestState?.sortDirection === SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC,
        isActive: false
      }));
    } else {
      dispatch(updateInactiveItemListRequest({
        ...itemListRequestState,
        sortBy: getSortFieldName(sorting[0].field),
        sortDirection:
          sorting[0].sort === "asc" ? SortDirection.ASC : SortDirection.DESC,
        isActive: false
      }));
    }
  };

  const onPaginationChange = (pagination: GridPaginationModel) => {
    dispatch(updateInactiveItemListRequest({
      ...itemListRequestState,
      pageIndex: pagination.page + 1,
      pageSize: pagination.pageSize,
      isActive: false
    }));
  };

  const handleApplyFilterClick = (filterValues: SelectedFilters) => {
    if (filterValues.projectCode) {
      setSelectedRows([]);
      setAllRecordsSelected(false);

      dispatch(updateInactiveItemListRequest({
        ...itemListRequestState,
        projectCode: filterValues.projectCode,
        pageIndex: 1,
        isActive: false
      }));
    } else if (Object.values(filterValues)?.length) {
      setSelectedRows([]);
      setAllRecordsSelected(false);

      dispatch(updateInactiveItemListRequest({
        pageSize: itemListRequestState.pageSize,
        sortBy: itemListRequestState.sortBy,
        sortDirection: itemListRequestState.sortDirection,
        ...filterValues,
        pageIndex: 1,
        isActive: false
      }));
    } else {
      //reset the filters
      dispatch(updateInactiveItemListRequest({...initialItemListRequest, isActive: false}));
      setAllRecordsSelected(false);
      setSelectedRows([]);
    }
  };

  const handleSelectAll = () => {
    const allRecordsSelectedNewValue = !allRecordsSelected;
    setAllRecordsSelected(allRecordsSelectedNewValue);
    // push current page's records into the selectedRows state so that grid shows them selected.
    // Other pages will be pushed to selectedRows as and when user nagivates through pages
    if (allRecordsSelectedNewValue === true) {
      const itemIDs = items.map(i => {
        return {
          id: i.id,
          stockCode: i?.stockcode,
          baseCode: i?.baseCode,
          pdStockCode: i?.pdStockcode
        };
      });
      setSelectedRows(selectedRows.concat(itemIDs.filter(ii => !selectedRows.some(sr => sr.id === ii.id))));
    }
    else {
      setSelectedRows([]);
    }
  };

  const handleExpandClick = () => {
    setFiltersExpanded(!filtersExpanded);
  };

  // Function to update product details with Row Edit via API call
  const updateRowApi = async (rowData) => {

    // Prepare the request payload with the row data
    const requestPayload = {
      id: rowData.id,
      stage: rowData.stage,
      status: rowData.status
    };

    try {
      api.put<ApiResponse<number>>('/Item/update-item-basic-detail', requestPayload)
        .then((response) => {
          if (response?.isSuccess) {
            toast.success(response.message);
          }
        })
        .catch((error) => console.error("Exception from Update Product detail API", error));
    } catch (err: any) {
      toast.error(err?.message);
    };
  };

  return (
    <>
      <ConfirmationModal
        isOpen={activateProductModal}
        onClose={handleCloseModal}
        title={"Confirm Activate Product"}
        message={"Are you sure you want to activate this product?"}
        handleConfirmYes={handleActivateProduct}
        handleConfirmNo={handleCloseModal}
      />
      <Filters
        onApplyFilter={handleApplyFilterClick}
        filtersExpanded={filtersExpanded}
        handleExpandClick={handleExpandClick}
        viewMode={PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS]}
      />
      <Loader isLoading={isLoading} />
      <Box
        sx={{
          height: "70%",
          width: "100%",
          display: "block",
        }}
      >
        <DataGrid
          rows={items}
          columns={[...stageStatusColumn, ...ProductListGridColumns]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10
              },
            },
            columns: {
              columnVisibilityModel: {
                updatedDate: false
              },
            },
          }}
          rowCount={productsTotalCount}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick={true}
          disableColumnFilter={true}
          sortingMode="server"
          paginationMode="server"
          sortModel={[
            {
              field: getReverseSortFieldName(itemListRequestState.sortBy),
              sort:
                itemListRequestState.sortDirection == SortDirection.ASC
                  ? "asc"
                  : "desc",
            },
          ]}
          onSortModelChange={onSortChange}
          onPaginationModelChange={onPaginationChange}
          paginationModel={{
            page: itemListRequestState.pageIndex - 1,
            pageSize: itemListRequestState.pageSize,
          }}
          slots={{
            toolbar: gridToolbar,
            noRowsOverlay: () => NO_ROWS_GRID_MESSAGE,
            noResultsOverlay: () => NO_LOCAL_SEARCH_RESULTS_GRID_MESSAGE,
          }}
          checkboxSelection={false}
          hideFooterSelectedRowCount={allRecordsSelected}
          getRowHeight={() => 'auto'}
        />
      </Box>
    </>
  );
}
